import React, { useState, useEffect } from 'react';
import Button from '../shared/Button';
import Modal from '../shared/Modal';
import ConfirmDialog from '../shared/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import {
    getNumberRanges,
    createNumberRangeConfig,
    updateNumberRangeConfig,
    deleteNumberRangeConfig
} from '../../services/firestore/sequenceService';

const NumberRangeConfig = ({ user }) => {
    const { success, error: toastError } = useToast();
    const { confirmConfig, showConfirm, closeConfirm } = useConfirmDialog();
    const [ranges, setRanges] = useState([]);
    const [loading, setLoading] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({});
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        if (user?.tenantId) {
            loadData();
        }
    }, [user?.tenantId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await getNumberRanges(user.tenantId);
            setRanges(data);
        } catch (err) {
            toastError('Veriler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditingId(item.id);
            setFormData(item);
        } else {
            setEditingId(null);
            setFormData({
                tenantId: user.tenantId,
                entityName: '', // e.g. DOCK_TYPE
                description: '',
                prefix: '',
                startValue: 1000,
                endValue: 9999,
                currentValue: 1000
            });
        }
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        if (!formData.entityName) {
            toastError('Nesne Anahtarı zorunludur (örn: DOCK_TYPE)');
            return;
        }

        try {
            if (editingId) {
                await updateNumberRangeConfig(editingId, formData);
                success('Güncellendi');
            } else {
                await createNumberRangeConfig(formData);
                success('Oluşturuldu');
            }
            setIsModalOpen(false);
            loadData();
        } catch (err) {
            toastError('İşlem başarısız: ' + err.message);
        }
    };

    const handleDelete = async (id) => {
        showConfirm({
            title: 'Numara Aralığını Sil',
            message: 'Bu numara aralığını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.',
            type: 'error',
            confirmText: 'Evet, Sil',
            onConfirm: async () => {
                try {
                    await deleteNumberRangeConfig(id);
                    success('Silindi');
                    loadData();
                } catch (err) {
                    toastError('Silinemedi');
                }
            }
        });
    };

    return (
        <div>
            <div className="flex justify-end mb-md">
                <Button variant="primary" onClick={() => handleOpenModal()}>+ Yeni Numara Aralığı</Button>
            </div>

            <div className="card" style={{ padding: 0 }}>
                {loading ? (
                    <div className="p-lg text-center">Yükleniyor...</div>
                ) : ranges.length === 0 ? (
                    <div className="p-lg text-center text-secondary">Kayıt yok.</div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Nesne Anahtarı</th>
                                <th>Açıklama</th>
                                <th>Format</th>
                                <th>Başlangıç / Bitiş</th>
                                <th>Güncel Sayaç</th>
                                <th>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ranges.map(r => (
                                <tr key={r.id}>
                                    <td style={{ fontWeight: 600 }}>{r.entityName}</td>
                                    <td>{r.description}</td>
                                    <td className="text-secondary">{r.prefix}XXXX</td>
                                    <td>{r.startValue} - {r.endValue}</td>
                                    <td style={{ fontWeight: 'bold' }}>{r.currentValue}</td>
                                    <td>
                                        <div className="flex gap-sm">
                                            <Button size="sm" variant="secondary" onClick={() => handleOpenModal(r)}>Düz.</Button>
                                            <Button size="sm" variant="danger" onClick={() => handleDelete(r.id)}>Sil</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingId ? "Numara Aralığı Düzenle" : "Yeni Numara Aralığı"}>
                <div className="form-group">
                    <label className="form-label">Nesne Anahtarı (Benzersiz)</label>
                    <input
                        className="input"
                        value={formData.entityName || ''}
                        onChange={e => setFormData({ ...formData, entityName: e.target.value })}
                        placeholder="Örn: DOCK_TYPE"
                        disabled={editingId}
                    />
                    <div className="text-xs text-secondary mt-xs">Sistemde kullanılan kod adıdır (DOCK_TYPE, RES_ID vb.)</div>
                </div>
                <div className="form-group">
                    <label className="form-label">Tanım / Açıklama</label>
                    <input
                        className="input"
                        value={formData.description || ''}
                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Örn: Rampa Tip Kodları"
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">Önek (Prefix)</label>
                    <input
                        className="input"
                        value={formData.prefix || ''}
                        onChange={e => setFormData({ ...formData, prefix: e.target.value })}
                        placeholder="Örn: DT-"
                    />
                </div>
                <div className="flex gap-md">
                    <div className="form-group flex-1">
                        <label className="form-label">Başlangıç Değeri</label>
                        <input
                            type="number"
                            className="input"
                            value={formData.startValue || ''}
                            onChange={e => setFormData({ ...formData, startValue: parseInt(e.target.value) })}
                        />
                    </div>
                    <div className="form-group flex-1">
                        <label className="form-label">Bitiş Değeri</label>
                        <input
                            type="number"
                            className="input"
                            value={formData.endValue || ''}
                            onChange={e => setFormData({ ...formData, endValue: parseInt(e.target.value) })}
                        />
                    </div>
                </div>
                {editingId && (
                    <div className="form-group bg-bg-secondary p-sm rounded">
                        <label className="form-label">Güncel Sayaç (Dikkatli Değiştirin)</label>
                        <input
                            type="number"
                            className="input"
                            value={formData.currentValue || ''}
                            onChange={e => setFormData({ ...formData, currentValue: parseInt(e.target.value) })}
                        />
                    </div>
                )}
                <div className="flex justify-end gap-md mt-lg">
                    <Button variant="secondary" onClick={() => setIsModalOpen(false)}>İptal</Button>
                    <Button variant="primary" onClick={handleSave}>Kaydet</Button>
                </div>
            </Modal>

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                onClose={closeConfirm}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                confirmText={confirmConfig.confirmText}
                cancelText={confirmConfig.cancelText}
            />
        </div>
    );
};

export default NumberRangeConfig;
